import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ChevronLeft, Mail, Smartphone } from "lucide-react";
import { useAuth } from "../lib/auth";
import PackagesPromo from "../components/PackagesPromo";
import AuthHeader from "../components/AuthHeader";

type Step = 1 | 2 | 3;

export default function Register() {
  const { user, requestRegistrationCode, verifyRegistrationCode, completeRegistration } = useAuth();
  // Dreistufige "E-Mail zuerst"-Registrierung:
  //   1 = E-Mail -> Code anfordern (noch kein Konto)
  //   2 = Bestätigungscode eingeben
  //   3 = Passwort (+ optional Name) setzen -> Konto + direkter Login
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  // Schritt 1: Code anfordern.
  async function handleRequestCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Bitte gib eine gültige E-Mail-Adresse ein");
      return;
    }
    setSubmitting(true);
    try {
      const status = await requestRegistrationCode(trimmed);
      if (status === "already_registered") {
        // Bereits registriert -> zur Anmeldung leiten (E-Mail vorausfüllen).
        navigate("/login", { state: { email: trimmed }, replace: false });
        return;
      }
      if (status === "mail_not_configured") {
        setError(
          "Der E-Mail-Versand ist derzeit nicht konfiguriert. Bitte versuche es später erneut oder wende dich an den Support.",
        );
        return;
      }
      setNotice(`Wir haben einen Bestätigungscode an ${trimmed} gesendet. Der Code ist 15 Minuten gültig.`);
      setCode("");
      setStep(2);
    } catch (err) {
      setError((err as Error).message || "Anfrage fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  }

  // Schritt 2: Code prüfen.
  async function handleVerifyCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError("Bitte gib den Bestätigungscode ein");
      return;
    }
    setSubmitting(true);
    try {
      await verifyRegistrationCode(email.trim(), trimmedCode);
      setStep(3);
    } catch (err) {
      const e2 = err as Error & { code?: string };
      setError(e2.message || "Der Code konnte nicht bestätigt werden");
      // Abgelaufen / aufgebraucht -> zurück zu Schritt 1 für einen neuen Code.
      if (e2.code === "expired" || e2.code === "too_many_attempts" || e2.code === "no_pending") {
        setStep(1);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Code erneut anfordern (aus Schritt 2).
  async function handleResendCode() {
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const status = await requestRegistrationCode(email.trim());
      if (status === "already_registered") {
        navigate("/login", { state: { email: email.trim() } });
        return;
      }
      if (status === "mail_not_configured") {
        setError("Der E-Mail-Versand ist derzeit nicht konfiguriert.");
        return;
      }
      setNotice("Wir haben dir einen neuen Code gesendet.");
    } catch (err) {
      setError((err as Error).message || "Anfrage fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  }

  // Schritt 3: Registrierung abschließen.
  async function handleComplete(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (password.length < 8) {
      setError("Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    setSubmitting(true);
    try {
      await completeRegistration({
        email: email.trim(),
        code: code.trim(),
        password,
        name: name || undefined,
      });
      // completeRegistration meldet direkt an -> Redirect via <Navigate> oben.
    } catch (err) {
      const e2 = err as Error & { code?: string };
      setError(e2.message || "Registrierung fehlgeschlagen");
      if (e2.code === "expired" || e2.code === "too_many_attempts" || e2.code === "no_pending") {
        setStep(1);
      } else if (e2.code === "mismatch") {
        setStep(2);
      } else if (e2.code === "already_registered") {
        navigate("/login", { state: { email: email.trim() } });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-frame">
        <AuthHeader mode="register" />

        {step === 1 ? (
          <form onSubmit={handleRequestCode} className="px-5 pt-4 pb-8 space-y-4 flex-1">
            <div>
              <label className="label" htmlFor="email">E-Mail</label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="input-email"
              />
            </div>
            {error && (
              <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2" data-testid="text-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting}
              data-testid="button-register-next"
            >
              {submitting ? "Wird gesendet …" : "Code anfordern"}
            </button>

            <a
              href="/expo"
              className="flex items-center justify-center gap-2 mt-2 w-full rounded-xl border border-[var(--color-app-border)] px-4 py-3 text-[14px] font-semibold text-[var(--color-brand-500)] hover:bg-[var(--color-app-bg-elevated,#fff)] transition-colors"
              data-testid="link-expo-preview"
            >
              <Smartphone size={18} />
              App auf dem Handy öffnen (Expo Go)
            </a>
          </form>
        ) : step === 2 ? (
          <form onSubmit={handleVerifyCode} className="px-5 pt-4 pb-8 space-y-4 flex-1">
            <div className="flex items-center gap-2 text-[13px] text-[var(--color-app-text-secondary)] bg-[var(--color-app-bg-elevated,#fff)] rounded-xl px-4 py-3">
              <Mail size={16} className="shrink-0 text-[var(--color-app-text-secondary)]" />
              <span className="font-semibold text-[var(--color-app-text)] truncate">{email.trim()}</span>
            </div>
            {notice && (
              <div className="flex items-start gap-2 text-sm text-[var(--color-app-text-secondary)] bg-[var(--color-caution-bg)] rounded-xl px-3 py-2" data-testid="text-notice">
                <Mail size={16} className="shrink-0 mt-0.5 text-[var(--color-caution)]" />
                <span>{notice}</span>
              </div>
            )}
            <div>
              <label className="label" htmlFor="code">Bestätigungscode</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
                maxLength={8}
                className="input tracking-[0.4em] text-center text-lg"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                data-testid="input-code"
              />
              <div className="text-[12px] text-[var(--color-app-text-tertiary)] mt-1">
                Der Code ist 15 Minuten gültig.
              </div>
            </div>
            {error && (
              <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2" data-testid="text-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting}
              data-testid="button-verify-code"
            >
              {submitting ? "Wird geprüft …" : "Code bestätigen"}
            </button>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={submitting}
              className="text-[14px] font-bold text-[var(--color-brand-500)] disabled:opacity-60 self-start hover:underline"
              data-testid="button-resend-code"
            >
              Code erneut senden
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setNotice(null);
                setStep(1);
              }}
              className="flex items-center justify-center gap-1 text-[14px] font-semibold text-[var(--color-app-text-secondary)] w-full pt-1 hover:underline"
              data-testid="button-register-back"
            >
              <ChevronLeft size={16} />
              Zurück
            </button>
          </form>
        ) : (
          <form onSubmit={handleComplete} className="px-5 pt-4 pb-8 space-y-4 flex-1">
            <div className="flex items-center gap-2 text-[13px] text-[var(--color-app-text-secondary)] bg-[var(--color-app-bg-elevated,#fff)] rounded-xl px-4 py-3">
              <Mail size={16} className="shrink-0 text-[var(--color-app-text-secondary)]" />
              <span className="font-semibold text-[var(--color-app-text)] truncate">{email.trim()}</span>
            </div>
            <div>
              <label className="label" htmlFor="name">Name <span className="text-[var(--color-app-text-tertiary)] font-normal">(optional)</span></label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="input-name"
              />
            </div>
            <div>
              <label className="label" htmlFor="password">Passwort</label>
              <input
                id="password"
                type="password"
                required
                autoFocus
                autoComplete="new-password"
                minLength={8}
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="input-password"
              />
              <div className="text-[12px] text-[var(--color-app-text-tertiary)] mt-1">
                Mindestens 8 Zeichen
              </div>
            </div>
            {error && (
              <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] rounded-xl px-3 py-2" data-testid="text-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting}
              data-testid="button-register"
            >
              {submitting ? "Wird erstellt …" : "Konto erstellen"}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setNotice(null);
                setStep(2);
              }}
              className="flex items-center justify-center gap-1 text-[14px] font-semibold text-[var(--color-app-text-secondary)] w-full pt-1 hover:underline"
              data-testid="button-register-back"
            >
              <ChevronLeft size={16} />
              Zurück
            </button>
          </form>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[12px] text-[var(--color-app-text-tertiary)]">
          <a href="/agb" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-agb">AGB</a>
          <span aria-hidden="true">·</span>
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-datenschutz">Datenschutz</a>
          <span aria-hidden="true">·</span>
          <a href="/widerruf" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-widerruf">Widerruf</a>
          <span aria-hidden="true">·</span>
          <a href="/impressum" target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="link-legal-impressum">Impressum</a>
        </div>

        <PackagesPromo />
      </div>
    </div>
  );
}
